import { Component, computed, inject, input, model, OnInit, output, signal, viewChild } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, FilterModel, GridOptions, ITextFilterParams } from 'ag-grid-community';
import { sortBy } from 'lodash';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { PanModalComponent } from '../../../components/pan-modal/pan-modal.component';
import { tryGetErrorMessage } from '../../../error.utils';
import { defaultTableDefOptions, localStorageSignal } from '../../../helpers';
import { BranchService } from '../../../service/branch.service';
import { BusinessVerticalService } from '../../../service/business-vertical.service';
import { NotifyService } from '../../../service/notify.service';
import { PanoptycAdminService } from '../../../service/panoptyc-admin.service';
import { UserLoginService } from '../../../service/user-login.service';
import { Branch, CustomerType } from '../../../types';
import { BusinessVertical } from '../../../types/business-vertical';
import { CellBranchesListActionsComponent } from './cell-branches-list-actions/cell-branches-list-actions.component';
import { CellBranchesListHeaderActionsComponent } from './cell-branches-list-header-actions/cell-branches-list-header-actions.component';

@Component({
  selector: 'app-bl-management-branches',
  templateUrl: './bl-management-branches.component.html',
  styleUrls: ['./bl-management-branches.component.scss'],
  standalone: false,
  providers: [],
})
export class BlManagementBranchesComponent implements OnInit {
  private userService = inject(UserLoginService);
  private adminService = inject(PanoptycAdminService);
  private branchService = inject(BranchService);
  private notifyService = inject(NotifyService);
  private verticalService = inject(BusinessVerticalService);
  public isLoading = input<boolean>(false);
  public branches = model.required<Branch[]>();
  public selectBranch = output<Branch>();

  public grid = viewChild<AgGridAngular>('branchesGrid');

  public customerTypes = signal<CustomerType[]>([]);
  public verticals = signal<BusinessVertical[]>([]);

  public newBranchModal = viewChild<PanModalComponent>('createNewBranch');
  public newBranchIsSubmitting = signal<boolean>(false);
  public newBranchCustomerType = signal<string | undefined>(undefined);
  public newBranchVertical = signal<string | undefined>(undefined);
  public newBranchName = signal<string | undefined>(undefined);

  public uiBranchNameFilter = localStorageSignal<FilterModel>(
    {
      filterType: 'text',
      type: 'contains',
      filter: '',
    },
    'bl-management-branches-ui-branch-name-filter',
  );

  public canCreateBranch = computed(
    () => !this.newBranchIsSubmitting() && this.newBranchCustomerType() && this.newBranchName() && this.newBranchVertical(),
  );
  public isSuspendedBillingOnly = computed(() => this.userService.currentProfile()?.access_suspended ?? false);

  public readonly tableDef: GridOptions = {
    ...defaultTableDefOptions(),
    pagination: true,
    paginationPageSize: 50,
    onGridSizeChanged: (event) => {
      event.api.sizeColumnsToFit();
    },
    onGridReady: (event) => {
      event.api.setFilterModel({
        name: { filterType: 'text', type: 'contains', ...this.uiBranchNameFilter() },
      });
    },
    onStateUpdated: (event) => {
      this.uiBranchNameFilter.set(event.api.getFilterModel().name ?? {});
    },
  };

  public readonly branchColumns: ColDef<Branch>[] = [
    {
      field: 'external_id',
      headerName: 'External ID',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
      width: 300,
      maxWidth: 300,
    },
    {
      field: 'name',
      headerName: 'Branch',
      cellDataType: 'text',
      minWidth: 200,
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
      filterParams: {
        buttons: ['reset'],
      } as ITextFilterParams,
    },
    {
      headerName: '',
      suppressColumnsToolPanel: true,
      suppressKeyboardEvent: () => true,
      sortable: false,
      width: 210,
      maxWidth: 210,
      cellClass: ['inline-flex', 'items-center', 'justify-end', 'gap-2'],
      headerComponent: CellBranchesListHeaderActionsComponent,
      headerClass: ['w-full'],
      headerComponentParams: {
        addCallback: () => {
          this.newBranchModal()?.show();
        },
      },
      cellRenderer: CellBranchesListActionsComponent,
      cellRendererParams: {
        billingOnly: (branch: Branch) => !this.userService.currentUserIsAdmin() && (branch.keystore?.suspend_access ?? false),
        editCallback: (data: Branch) => {
          this.selectBranch.emit(data);
        },
      },
    },
  ];

  ngOnInit() {
    if (this.userService.currentUserIsAdmin()) {
      forkJoin({
        customerTypesResponse: this.adminService.getCustomerTypes(),
        businessVerticalsResponse: this.verticalService.getAllBusinessVerticals(),
      }).subscribe(({ customerTypesResponse, businessVerticalsResponse }) => {
        this.customerTypes.set(sortBy(customerTypesResponse, 'name'));
        this.verticals.set(sortBy(businessVerticalsResponse.items, 'name'));
      });
    }
  }

  createBranch() {
    const name = this.newBranchName();
    const customerType = this.newBranchCustomerType();
    const vertical = this.newBranchVertical();

    if (!name || !customerType || !vertical) return;

    this.newBranchIsSubmitting.set(true);
    this.branchService
      .createBranch({
        name: name,
        customer_type_id: customerType,
        business_vertical_id: vertical,
      })
      .subscribe((newBranch) => {
        this.newBranchIsSubmitting.set(false);
        this.newBranchCustomerType.set(undefined);
        this.newBranchName.set(undefined);
        this.newBranchVertical.set(undefined);
        this.newBranchModal()?.hide();

        this.branches.update((currentBranches) => [...currentBranches, newBranch]);

        this.grid()?.api.setFilterModel({
          name: {
            filterType: 'text',
            type: 'contains',
            filter: newBranch.name,
          },
        });
      });
  }

  uploadShrinkCSVToServer(element: EventTarget): void {
    const files = (element as HTMLInputElement).files ?? new FileList();
    const file = files.item(0);
    if (!file) return;

    this.branchService
      .uploadBranchesShrinkCsv({
        csv: file,
      })
      .pipe(finalize(() => ((element as HTMLInputElement).value = '')))
      .subscribe({
        next: () => this.notifyService.notify('Shrink uploaded successfully'),
        error: (err) => this.notifyService.notify(`Error uploading shrink: ${tryGetErrorMessage(err)}`),
      });
  }
}
